import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, UtensilsCrossed, CalendarDays, Wine, Phone, MapPin } from "lucide-react";
import { LanguageSwitcher } from "./LanguageSwitcher";

const NAV_LINKS = [
  { href: "#accueil", key: "nav.home" },
  { href: "#carte", key: "nav.menu" },
  { href: "#evenements", key: "nav.events" },
  { href: "#caviste", key: "nav.cellar" },
  { href: "#reservation", key: "nav.reserve" },
  { href: "#equipe", key: "nav.team" },
  { href: "#galerie", key: "nav.gallery" },
  { href: "#partenaires", key: "nav.partners" },
  { href: "#nous-trouver", key: "nav.contact" },
];

const MOBILE_BAR_LINKS = [
  { href: "#carte", key: "nav.menu", icon: UtensilsCrossed },
  { href: "#evenements", key: "nav.events", icon: CalendarDays },
  { href: "#caviste", key: "nav.cellar", icon: Wine },
  { href: "#reservation", key: "nav.reserve", icon: Phone },
  { href: "#nous-trouver", key: "nav.contact", icon: MapPin },
];

export const Navigation = () => {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNavClick = (e, href) => {
    e.preventDefault();
    setMobileOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
    <nav
      data-testid="main-navigation"
      className={`fixed top-0 w-full z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#FDFBF7]/92 backdrop-blur-md shadow-sm border-b border-[#D8D0C5]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16 md:h-20">
        {/* Logo */}
        <a
          href="#accueil"
          onClick={(e) => handleNavClick(e, "#accueil")}
          className="flex items-center gap-2"
          data-testid="nav-logo"
        >
          <img
            src="/logo.png"
            alt="L'Ancienne Poste"
            className="h-10 md:h-12 w-auto"
          />
        </a>

        {/* Desktop Links + Switcher */}
        <div className="hidden md:flex items-center gap-6">
          <div className="flex items-center gap-6">
            {NAV_LINKS.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => handleNavClick(e, link.href)}
                data-testid={`nav-link-${link.href.slice(1)}`}
                className="text-xs uppercase tracking-[0.15em] transition-colors duration-300 hover:opacity-70"
                style={{
                  color: scrolled ? "#5D544F" : "rgba(255,255,255,0.9)",
                  fontFamily: "'Lato', sans-serif",
                  fontWeight: 400,
                }}
              >
                {t(link.key)}
              </a>
            ))}
          </div>
          <LanguageSwitcher variant={scrolled ? "light" : "dark"} />
        </div>

        {/* Mobile Toggle */}
        <div className="md:hidden flex items-center gap-2">
          <LanguageSwitcher variant={scrolled ? "light" : "dark"} />
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="p-2"
            data-testid="mobile-menu-toggle"
            style={{ color: scrolled ? "#2C2420" : "white" }}
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-[#FDFBF7] border-b border-[#D8D0C5] overflow-hidden"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {NAV_LINKS.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => handleNavClick(e, link.href)}
                  data-testid={`mobile-nav-link-${link.href.slice(1)}`}
                  className="text-sm uppercase tracking-[0.2em] text-[#5D544F] hover:text-[#C1624D] transition-colors duration-300"
                  style={{ fontFamily: "'Lato', sans-serif" }}
                >
                  {t(link.key)}
                </a>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>

    {/* Mobile Bottom Bar - app-like */}
    <div
      className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-[#FDFBF7]/95 backdrop-blur-md 
                 border-t border-[#D8D0C5] shadow-[0_-2px_10px_rgba(0,0,0,0.05)]"
      data-testid="mobile-bottom-bar"
      style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
    >
      <div className="flex items-center justify-around py-2">
        {MOBILE_BAR_LINKS.map((link) => {
          const Icon = link.icon;
          return (
            <a
              key={link.href}
              href={link.href}
              onClick={(e) => handleNavClick(e, link.href)}
              className="flex flex-col items-center gap-0.5 py-1 px-2 text-[#8C847F] 
                         active:text-[#C1624D] transition-colors duration-200"
              data-testid={`bottom-bar-${link.href.slice(1)}`}
            >
              <Icon size={20} />
              <span className="text-[10px] tracking-wide" style={{ fontFamily: "'Lato', sans-serif" }}>
                {t(link.key)}
              </span>
            </a>
          );
        })}
      </div>
    </div>
    </>
  );
};
